import { isEmpty } from 'lodash';
import { put, select, takeEvery } from 'redux-saga/effects';
import { RootState } from '../..';
import { REPORTING_CONSTANTS } from '../../../modules/reportingAndAnalytics/reportingUtils/reportingAndAnalyticsConstants';
import { COMMON_ERROR } from '../../../shared/constants/commonConstants';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';
import SendRequest from '../../../shared/services/sendRequest.service';
import {
  buildUpdatedFilters,
  getDateRangeWithOffset,
} from '../../../utils/reportingUtility';
import { centeredNotification } from '../../../utils/utility';

import {
  setReportingConfig,
} from '../../actions/reportingAndAnalyticsActions';

export function* fetchReportingTransactionMeta({ payload }: any): Generator {
  const { successFullMetaCallback, tabName, successMetaData } = payload;
  const sendRequest = SendRequest.instance;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Report/FetchTransactionMetaData`;
    const response: any = yield sendRequest.post<any, any>(url, { tabName });
    const { isSuccess } = response;
    if (isSuccess) {
      successFullMetaCallback(isSuccess);
      successMetaData(response);
    }
  } catch (e) {
    successFullMetaCallback(false);
    centeredNotification(COMMON_ERROR);
  }
}

export function* fetchTransactionData({ payload }: any): Generator {
  const sendRequest = SendRequest.instance;
  const { successCallback, configuredPayers, availableStatus } = payload;

  try {
    const { reportingDateRange, transactionReportFilters }: any = yield select(
      (state: RootState): any => state.reportingAndAnalytics,
    );

    const updatedFilters = buildUpdatedFilters(
      transactionReportFilters,
      configuredPayers,
      availableStatus,
    );
    const dateRangeSelected = isEmpty(reportingDateRange);
    const { startDate, endDate } = getDateRangeWithOffset(
      transactionReportFilters?.dateFilter,
    );
    const request = {
      filters: updatedFilters,
      startDate: dateRangeSelected ? startDate : reportingDateRange.startDate,
      endDate: dateRangeSelected ? endDate : reportingDateRange.endDate,
    };
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Report/FetchTransactionData`;
    const response: any = yield sendRequest.post<any, any>(url, request);
    const {
      transactionData,
      dataAvailableFrom,
      dataRefreshesOn,
      lastRefreshedOn,
    } = response;
    successCallback(
      transactionData,
      dataAvailableFrom,
      dataRefreshesOn,
      lastRefreshedOn,
    );
  } catch (e) {
    centeredNotification(COMMON_ERROR);
  }
}

export function* FetchReportingConfig({ payload }: any): any {
  const { tabName } = payload;
  const sendRequest = SendRequest.instance;

  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Report/FetchReportingConfig`;

    const response: any = yield sendRequest.post<any, any>(url, { tabName });
    const {
      isSuccess,
      reportingEmail,
      schedulerEmail,
      schedulerFilters,
      selectedSchedulerConfig,
      onRequestFilters,
      onRequestReportingEmail,
    } = response || {};

    if (isSuccess) {
      yield put(
        setReportingConfig({
          reportingEmail: reportingEmail ?? [],
          schedulerEmail: schedulerEmail ?? [],
          schedulerFilters: schedulerFilters ?? [],
          selectedSchedulerConfig: selectedSchedulerConfig ?? [],
          onRequestFilters: onRequestFilters ?? [],
          onRequestReportingEmail: onRequestReportingEmail ?? [],
        }),
      );
    }
  } catch (e) {
    centeredNotification(COMMON_ERROR);
  }
}

export function* exportReportData({ payload }: any): Generator {
  const sendRequest = SendRequest.instance;
  const { callBack, requestPayload } = payload;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Report/ExportReportData`;
    const { reportingDateRange, transactionReportFilters }: any = yield select(
      (state: RootState): any => state.reportingAndAnalytics,
    );
    const isTATDailyReport =
      (requestPayload.tabName === REPORTING_CONSTANTS.TRANSACTION_TAT) &&
      (transactionReportFilters.dateFilter === REPORTING_CONSTANTS.ZERO_STRING);

    const dateRangeConfig = isTATDailyReport
      ? REPORTING_CONSTANTS.TAT_DAILY
      : transactionReportFilters?.dateFilter;

    const { startDate, endDate } = getDateRangeWithOffset(dateRangeConfig);
    const isDateRangeEmpty = isEmpty(reportingDateRange);

    const reportRequest =
      (requestPayload.ConfigType === REPORTING_CONSTANTS.ONREQUEST_REPORT)
        ? requestPayload
        : {
          ...requestPayload,
          startDate: isDateRangeEmpty
            ? startDate
            : reportingDateRange.startDate,
          endDate: isDateRangeEmpty ? endDate : reportingDateRange.endDate,
        };

    const response: any = yield sendRequest.post<any, any>(url, reportRequest);
    const { isSuccess, message } = response;
    callBack(isSuccess, message);
  } catch (e) {
    callBack(false, COMMON_ERROR);
    centeredNotification(COMMON_ERROR);
  }
}

export function* fetchTatData({ payload }: any): Generator {
  const sendRequest = SendRequest.instance;
  const { successCallback, configuredPayers, availableStatus } = payload;

  try {
    const { reportingDateRange, transactionReportFilters }: any = yield select(
      (state: RootState): any => state.reportingAndAnalytics,
    );

    const updatedFilters = buildUpdatedFilters(
      transactionReportFilters,
      configuredPayers,
      availableStatus,
    );
    const dateRangeSelected = isEmpty(reportingDateRange);
    const dateFilter = transactionReportFilters?.dateFilter === REPORTING_CONSTANTS.ZERO_STRING ?
    REPORTING_CONSTANTS.TAT_DAILY : transactionReportFilters?.dateFilter;
    const { startDate, endDate } = getDateRangeWithOffset(
      dateFilter,
    );
    const request = {
      filters: updatedFilters,
      startDate: dateRangeSelected ? startDate : reportingDateRange.startDate,
      endDate: dateRangeSelected ? endDate : reportingDateRange.endDate,
    };

    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Report/FetchTATData`;
    const response: any = yield sendRequest.post<any, any>(url, request);
    const {
      transactionData,
      dataAvailableFrom,
      dataRefreshesOn,
      lastRefreshedOn,
    } = response;
    successCallback(
      transactionData,
      dataAvailableFrom,
      dataRefreshesOn,
      lastRefreshedOn,
    );
  } catch (e) {
    centeredNotification(COMMON_ERROR);
  }
}

export function* watchReportingAndAnalyticsAction(): any {
  yield takeEvery(
    'FETCH_REPORTING_TRANSACTION_META',
    fetchReportingTransactionMeta,
  );
  yield takeEvery('FETCH_TRANSACTION_DATA', fetchTransactionData);
  yield takeEvery('FETCH_TAT_DATA', fetchTatData);
  yield takeEvery('FETCH_REPORTING_CONFIG', FetchReportingConfig);
  yield takeEvery('EXPORT_REPORT_DATA', exportReportData);
}
export default watchReportingAndAnalyticsAction;
